import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { setAuthToken, setUser, getAuthToken, isAuthenticated, getAuthConfig, logout, setCurrentService, getUser } from '../utils/auth';
import AppNav from '../components/AppNav';
import './PlanSelection.css';


// Proper display names for known services
const SERVICE_DISPLAY_NAMES = {
  n8n: 'n8n', wordpress: 'WordPress', ghost: 'Ghost', openclaw: 'OpenClaw',
  nextcloud: 'Nextcloud', gitea: 'Gitea', mattermost: 'Mattermost',
  plausible: 'Plausible', appsmith: 'Appsmith', supabase: 'Supabase',
};
const displayService = (key) =>
  SERVICE_DISPLAY_NAMES[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '');

function PlanSelectionPage() {
  const navigate = useNavigate();
  const { service } = useParams();
  const location = useLocation();
  const changePlanSub   = location.state?.changePlanSub   || null;
  const resumeIncomplete = location.state?.resumeIncomplete || null;

  // Store the current service in localStorage for pages that don't have :service in URL
  useEffect(() => {
    if (service) {
      setCurrentService(service);
    }
  }, [service]);
  const [emailVerified, setEmailVerified] = useState(true);
  const [customerStatus, setCustomerStatus] = useState(null);
  // True when any subscription has a pending plan change awaiting admin approval.
  const hasPendingChange = (customerStatus?.activeSubscriptions || []).some(
    s => s.serviceStatus === 'pending_upgrade' || s.hasPendingUpgrade
  );
  const [plans, setPlans] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [planOrder, setPlanOrder] = useState([]); // Dynamic plan order from server

  // Compute how much cheaper yearly is vs paying monthly for a full year (e.g. 25%)
  const yearlySavingsPercent = (() => {
    const eligible = plans.filter(p => p.yearlyPrice && p.price && p.price > 0);
    if (eligible.length === 0) return null;
    const avg = eligible.reduce((sum, p) => sum + (p.price * 12 - p.yearlyPrice) / (p.price * 12), 0) / eligible.length;
    return Math.round(avg * 100);
  })();
  const maxTotalDiscount = (() => {
    const eligible = plans.filter(p => p.price > 0 && p.yearlyPrice > 0);
    if (eligible.length === 0) return null;
    return Math.max(...eligible.map(p => {
      const baseDiscount = Math.round(((p.price * 2 - p.price) / (p.price * 2)) * 100);
      const yearlyExtra = Math.round(((p.price - p.yearlyPrice / 12) / p.price) * 100);
      return baseDiscount + yearlyExtra;
    }));
  })();
  const [serviceInfo, setServiceInfo] = useState(null); // Service metadata from API
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [billingInterval, setBillingInterval] = useState(() => {
    // Default to the current subscription's interval so the toggle opens on the right tab
    const sub = location.state?.changePlanSub;
    if (sub?.billingInterval === 'month') return 'monthly';
    return 'yearly';
  });

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  // Check for existing authentication on mount
  useEffect(() => {
    const init = async () => {
      if (isAuthenticated()) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/verify`, getAuthConfig());
          if (response.data.success) {
            setEmailVerified(response.data.user.emailVerified || false);
            await fetchCustomerStatus();
          }
        } catch (err) {
          logout();
        }
      }
      if (!isAuthenticated()) {
        // Plans are behind login — redirect and let them come back after auth
        navigate(`/login?next=/${service}`, { replace: true });
        setIsCheckingAuth(false);
        return;
      }
      fetchPlans();
      setIsCheckingAuth(false);
    };
    init();
  }, []);

  // Refresh subscription data when window regains focus (e.g., user returns from payment)
  useEffect(() => {
    const handleFocus = async () => {
      if (isAuthenticated()) {
        await fetchCustomerStatus();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Auto-navigate to checkout when resuming an incomplete checkout (from SubscriptionsPage)
  useEffect(() => {
    if (!resumeIncomplete || !plansLoaded || plans.length === 0) return;
    const ic = resumeIncomplete;
    const domainType = ic.domain?.includes('.') && !ic.domain?.endsWith('.openhosst.com') ? 'custom' : 'subdomain';
    const navState = {
      action: 'new',
      billingInterval: ic.interval === 'year' ? 'yearly' : 'monthly',
      domainChoice: domainType,
      domain: domainType === 'custom' ? ic.domain : undefined,
      subdomain: domainType === 'subdomain' ? (ic.domain?.split('.')[0] || '') : undefined,
    };
    navigate(`/checkout/${ic.planSlug}`, { state: navState, replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansLoaded]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      // Fetch plans filtered by service if available, fallback to all plans
      const endpoint = service ? `${API_URL}/api/services/${service}/plans` : `${API_URL}/api/plans`;
      const response = await axios.get(endpoint);
      setPlans(response.data.plans || response.data); // Support both old and new format
      setPlanOrder(response.data.planOrder || []); // Get plan order from server
      setServiceInfo(response.data.service || null);
    } catch (err) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
      setPlansLoaded(true);
    }
  };

  const fetchCustomerStatus = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/check-customer`, {}, getAuthConfig());

      setCustomerStatus(response.data);

    } catch (err) {

      setError('Failed to load customer data');
    }
  };






  const handlePlanSelect = (planSlug, action = 'new') => {
    // Navigate with state including billing interval and current subscription ID for upgrades
    const navState = { action, billingInterval };
    if (changePlanSub && (action === 'upgrade' || action === 'downgrade' || action === 'billing_change')) {
      navState.currentSubscriptionId = changePlanSub.id;
    }
    // Billing cycle switch: skip domain page entirely, use existing domain
    if (action === 'billing_change' && changePlanSub) {
      navState.domainChoice = changePlanSub.domainType || 'subdomain';
      navState.domain = changePlanSub.domain || '';
      navState.subdomain = changePlanSub.subdomain || '';
      navigate(`/checkout/${planSlug}`, { state: navState });
      return;
    }
    // Upgrade/downgrade: pass existing domain data so domain page can pre-fill without re-fetching
    if ((action === 'upgrade' || action === 'downgrade') && changePlanSub) {
      navState.existingDomain = {
        domain: changePlanSub.domain || null,
        domainType: changePlanSub.domainType || null,
        subdomain: changePlanSub.subdomain || null,
      };
    }
    navigate(`/domain/${planSlug}`, { state: navState });
  };

  const handleCancelSubscription = (subscription) => {
    // Navigate to cancel page with subscription details
    navigate(`/${service}/cancel-subscription`, { state: { subscription } });
  };

  const handleDowngrade = (newPlan, currentSubscription) => {
    // Navigate to downgrade page with plan and subscription details
    navigate(`/${service}/downgrade-subscription`, {
      state: {
        newPlan,
        currentSubscription
      }
    });
  };

  const handleLogout = () => {
    logout();
    setCustomerStatus(null);
    setPlans([]);
    navigate('/login');
  };

  // Get subscriptions for the current service
  const getServiceSubscriptions = () => {
    if (!customerStatus?.activeSubscriptions) return [];
    return customerStatus.activeSubscriptions.filter(sub => {
      // Filter by service if subscription has service info, or by plan slug prefix
      if (sub.service) return sub.service === service;
      if (sub.planSlug) return sub.planSlug.startsWith(service);
      return true; // Fallback: include all if no service info
    });
  };

  const getCurrentPlanSlug = () => {
    // In change plan mode use the specific subscription; otherwise use first service sub
    const serviceSubscriptions = changePlanSub ? [changePlanSub] : getServiceSubscriptions();
    if (serviceSubscriptions.length === 0) return null;

    const subscription = serviceSubscriptions[0];

    // FIRST: Match by planSlug from Stripe metadata — stable across price changes
    if (subscription.planSlug) {
      const planBySlug = plans.find(p => p.slug === subscription.planSlug);
      if (planBySlug) return planBySlug.slug;
    }

    // SECOND: Match by price ID — covers subscriptions missing planSlug metadata
    const subscriptionPriceId = subscription.priceId;
    for (const plan of plans) {
      if (plan.priceId === subscriptionPriceId || plan.yearlyPriceId === subscriptionPriceId) {
        return plan.slug;
      }
    }

    // THIRD: Match by plan name keywords — last resort
    const subscriptionPlanName = (subscription.planName || '').toLowerCase();
    for (const plan of plans) {
      const planNameWords = plan.name.toLowerCase().split(' ').filter(w => w.length > 2);
      if (planNameWords.some(word => subscriptionPlanName.includes(word))) {
        return plan.slug;
      }
    }

    return null;
  };

  const canUpgradeTo = (planSlug) => {
    const currentSlug = getCurrentPlanSlug();
    if (!currentSlug || planOrder.length === 0) return false;

    const currentIndex = planOrder.indexOf(currentSlug);
    const targetIndex = planOrder.indexOf(planSlug);

    return targetIndex > currentIndex;
  };

  const canDowngradeTo = (planSlug) => {
    const currentSlug = getCurrentPlanSlug();
    if (!currentSlug || planOrder.length === 0) return false;

    const currentIndex = planOrder.indexOf(currentSlug);
    const targetIndex = planOrder.indexOf(planSlug);

    return targetIndex < currentIndex;
  };

  const isCurrentPlan = (planSlug, checkBillingInterval = true) => {
    const currentSlug = getCurrentPlanSlug();

    if (currentSlug !== planSlug) {
      return false;
    }

    // If we're not checking billing interval, just return true
    if (!checkBillingInterval) {
      return true;
    }

    // Check if the subscription's billing interval matches the selected interval
    const subscription = getSubscriptionsForPlan(planSlug)[0];
    if (!subscription) {

      return false;
    }

    const plan = plans.find(p => p.slug === planSlug);
    if (!plan) {

      return false;
    }

    // In change plan mode always compare against the specific subscription being changed,
    // not the first result from getSubscriptionsForPlan (which could be a pending_upgrade sub).
    const refSub = changePlanSub || subscription;

    // Use billingInterval from Stripe directly — robust against price ID changes
    let matches = false;
    if (billingInterval === 'yearly') {
      matches = refSub.billingInterval === 'year';
    } else {
      matches = refSub.billingInterval === 'month';
    }

    return matches;
  };

  // Check if user can change billing interval (same plan, different interval)
  const canChangeBillingInterval = (planSlug) => {
    if (getCurrentPlanSlug() !== planSlug) return false;

    // In change plan mode use changePlanSub directly — avoids picking up a pending_upgrade
    // sub that was created more recently and would appear first in getSubscriptionsForPlan.
    const subscription = changePlanSub || getSubscriptionsForPlan(planSlug)[0];
    if (!subscription) return false;

    const plan = plans.find(p => p.slug === planSlug);
    if (!plan) return false;

    // Can change if current subscription interval doesn't match selected interval
    // Use billingInterval from Stripe directly — robust against price ID changes
    if (billingInterval === 'yearly') {
      return subscription.billingInterval !== 'year' && !!plan.yearlyPriceId;
    } else {
      return subscription.billingInterval !== 'month';
    }
  };

  const getSubscriptionsForPlan = (planSlug) => {
    if (!customerStatus?.activeSubscriptions) return [];

    const targetPlan = plans.find(p => p.slug === planSlug);
    if (!targetPlan) return [];

    const planNameWords = targetPlan.name.toLowerCase().split(' ').filter(w => w.length > 2);
    return customerStatus.activeSubscriptions.filter(sub => {
      // Primary: planSlug match — stable across price changes
      if (sub.planSlug === planSlug) return true;
      // Secondary: priceId match
      if (sub.priceId && (sub.priceId === targetPlan.priceId || sub.priceId === targetPlan.yearlyPriceId)) return true;
      // Tertiary: plan name keywords
      return planNameWords.some(word => (sub.planName || '').toLowerCase().includes(word));
    });
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={handleLogout} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  // Redirect to verification page if email is not verified
  if (isAuthenticated() && !emailVerified) {
    navigate('/verify-email');
    return null;
  }

  // Show loading while fetching data for authenticated users
  if (isAuthenticated() && (!customerStatus || (plans.length === 0 && !plansLoaded))) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={handleLogout} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
        <p className="loading-text">Loading your plans...</p>
      </div>
    );
  }

  // Plans loaded but none exist for this service
  if (plansLoaded && plans.length === 0) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={handleLogout} />
        <div className="loading-container" style={{ flexDirection: 'column', gap: '16px' }}>
          <i className="fa-solid fa-box-open" style={{ fontSize: '48px', color: '#444' }} />
          <p className="loading-text" style={{ margin: 0 }}>No plans available yet for this service.</p>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>Check back soon or <a href="/subscriptions?tab=support" style={{ color: '#40E0D0' }}>contact support</a>.</p>
        </div>
      </div>
    );
  }

  if (loading && plans.length === 0) {
    return (
      <div className="checkout-container-new">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Real Trustpilot reviews
  const reviews = [
    {
      name: 'Mónica T.', country: 'MX', rating: 5,
      title: 'Excellent for n8n with unmatched human support',
      text: "I've been using OpenHosst primarily to host my n8n instances, and the experience has been flawless. They aren't your typical hosting company that replies with automated messages — whenever I had a question, the team was extremely prompt with real solutions.",
      date: 'Feb 20, 2026',
    },
    {
      name: 'Devlop F.', country: 'DZ', rating: 5,
      title: 'Amazing technical support',
      text: 'Amazing technical support, fast response, and guaranteed problem resolution. You are the best.',
      date: 'Feb 20, 2026',
    },
    {
      name: 'Joel A.', country: 'MX', rating: 5,
      title: 'Awesome customer service',
      text: 'Awesome customer service. The response is always quick and accurate even with very special situations.',
      date: 'Feb 15, 2026',
    },
    {
      name: 'Custodian AI', country: 'NZ', rating: 5,
      title: 'Very Responsive Customer Support',
      text: 'Very Responsive Customer Support. Fast, knowledgeable assistance every time I reach out.',
      date: 'Feb 15, 2026',
    },
    {
      name: 'Nathalie N.', country: 'EC', rating: 5,
      title: 'Awesome customer service',
      text: "I have been using the trial version and so far it has met and exceeded all expectations. The customer support alone is worth it — genuinely helpful every time.",
      date: 'Feb 11, 2026',
    },
    {
      name: 'Vijay G.', country: 'IN', rating: 5,
      title: 'Awesome product and personalised support',
      text: 'Awesome product and more than the product the customised customer service is too good. Happy to be part of this company and I will definitely recommend everyone.',
      date: 'Feb 10, 2026',
    },
  ];

  const currentUser = getUser();
  const fullName = currentUser?.fullName || '';

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />

      <div className="plan-selection-container">

        {/* ── HERO BANNER ── */}
        <div className="hero-banner">
          <div className="hero-banner-inner">
            <div className="hero-badge">
              <i className="fa-solid fa-bolt" style={{ fontSize: '10px' }} />
              LIMITED TIME OFFER
            </div>
            <h1 className="hero-title">
              Save Up to <span className="hero-accent">{maxTotalDiscount || 75}%</span> on Managed {serviceInfo?.name || displayService(service) || 'n8n'} Hosting
            </h1>
            <p className="hero-subtitle">
              Enterprise-grade infrastructure at startup prices. Deploy in minutes, scale without limits.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-number">99.9%</span>
                <span className="hero-stat-label">Uptime SLA</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-number">&lt;2hr</span>
                <span className="hero-stat-label">Avg. Activation</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-number">1,000+</span>
                <span className="hero-stat-label">Active Instances</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-number">4.5★</span>
                <span className="hero-stat-label">Trustpilot Rating</span>
              </div>
            </div>
          </div>
          <div className="hero-banner-glow" />
        </div>

        {/* ── PAGE TITLE + BILLING TOGGLE ── */}
        <div className="plan-selection-header">
          <h2 className="plan-selection-title">
            {changePlanSub
              ? 'Select Your New Plan'
              : `Choose Your ${serviceInfo?.name || displayService(service) || 'n8n'} Plan`}
          </h2>
          <p className="plan-selection-subtitle">
            {changePlanSub
              ? `Changing plan for: ${changePlanSub.domain || changePlanSub.planName}`
              : `All plans include full managed setup, backups & priority support`}
          </p>

          <div className="billing-toggle-container">
            <button
              className={`billing-toggle-btn ${billingInterval === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </button>
            <button
              className={`billing-toggle-btn ${billingInterval === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingInterval('yearly')}
            >
              Yearly
              {yearlySavingsPercent ? (
                <span className="savings-badge">Get Additional {yearlySavingsPercent}% Off</span>
              ) : (
                <span className="savings-badge">Save more</span>
              )}
            </button>
          </div>
        </div>

        {/* ── TRUST BAR ── */}
        <div className="trust-bar">
          <div className="trust-item">
            <i className="fa-solid fa-shield-halved" style={{color:'#4ade80'}} />
            <span>Price Lock Guarantee</span>
          </div>
          <div className="trust-bar-sep" />
          <div className="trust-item">
            <i className="fa-brands fa-stripe" style={{color:'#40E0D0'}} />
            <span>Secured by Stripe</span>
          </div>
          <div className="trust-bar-sep" />
          <div className="trust-item">
            <i className="fa-solid fa-rotate-left" style={{color:'#40E0D0'}} />
            <span>Cancel Anytime</span>
          </div>
          <div className="trust-bar-sep" />
          <div className="trust-item">
            <i className="fa-solid fa-headset" style={{color:'#60a5fa'}} />
            <span>Expert Support</span>
          </div>
        </div>

        {/* ── PENDING / CHANGE-PLAN BANNERS ── */}
        {changePlanSub && hasPendingChange && (
          <div className="existing-subs-banner" style={{ background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b' }}>
            <span style={{ color: '#f59e0b' }}>
              <i className="fa-solid fa-clock" style={{ marginRight: 6 }}></i>
              You have a pending plan change awaiting admin approval. Upgrades and downgrades are locked until it is reviewed.
            </span>
          </div>
        )}

        {changePlanSub ? (
          <div className="existing-subs-banner">
            <span>
              Changing plan for: <strong style={{color: '#fff'}}>{changePlanSub.domain || changePlanSub.planName}</strong>
              {changePlanSub.planName && ` — currently on ${changePlanSub.planName}`}
            </span>
            <Link to="/subscriptions" className="banner-link"><i className="fa-solid fa-arrow-left" style={{marginRight:4}}></i> Cancel</Link>
          </div>
        ) : getServiceSubscriptions().length > 0 && (
          <div className="existing-subs-banner">
            <span>
              <i className="fa-solid fa-circle-check" style={{marginRight:4}}></i> You have {getServiceSubscriptions().length} active {serviceInfo?.name || displayService(service)} subscription{getServiceSubscriptions().length > 1 ? 's' : ''}.
            </span>
            <Link to="/subscriptions" className="banner-link">View My Subscriptions <i className="fa-solid fa-arrow-right" style={{marginLeft:4}}></i></Link>
          </div>
        )}

        {/* ── PRICE LOCK BANNER ── */}
        <div className="price-lock-banner">
          <i className="fa-solid fa-lock" />
          <span>
            <strong>Price Lock Guarantee:</strong> Subscribe today and your price stays the same for {billingInterval === 'yearly' ? '2 full years' : '6 months'} — no surprise increases.
          </span>
        </div>

        {/* ── PLANS GRID ── */}
        <div className="plans-grid">
          {plans.map((plan, index) => {
            const currentMonthlyPrice = plan.price;
            const currentYearlyPrice = plan.yearlyPrice;
            const originalMonthlyPrice = (currentMonthlyPrice * 2).toFixed(2);
            const monthlyYearTotal = (currentMonthlyPrice * 12).toFixed(2);
            const originalYearlyPrice = (parseFloat(originalMonthlyPrice) * 12).toFixed(2);
            const yearlyPricePerMonth = currentYearlyPrice ? (Math.floor(currentYearlyPrice / 12 * 100) / 100).toFixed(2) : null;
            const displayPrice = billingInterval === 'yearly' ? currentYearlyPrice : currentMonthlyPrice;
            const originalPrice = billingInterval === 'yearly' ? originalYearlyPrice : originalMonthlyPrice;
            const pricePerMonth = billingInterval === 'yearly' ? yearlyPricePerMonth : currentMonthlyPrice;
            const monthlyDiscountPercent = Math.round(((parseFloat(originalMonthlyPrice) - currentMonthlyPrice) / parseFloat(originalMonthlyPrice)) * 100);
            const yearlyExtraPercent = currentYearlyPrice && currentMonthlyPrice > 0
              ? Math.round(((currentMonthlyPrice - currentYearlyPrice / 12) / currentMonthlyPrice) * 100) : 0;
            const yearlyDiscountPercent = monthlyDiscountPercent + yearlyExtraPercent;
            const yearlySaveVsMonthly = currentYearlyPrice ? (parseFloat(originalYearlyPrice) - currentYearlyPrice).toFixed(2) : '0.00';
            const isRecommended = plan.isPopular || false;

            return (
              <div key={plan.slug} className={`plan-card-selection ${isRecommended ? 'recommended' : ''}`}>

                {isRecommended ? (
                  <div className="best-choice-badge">
                    <i className="fa-solid fa-fire" />
                    Most Popular
                    <span className="best-choice-discount">
                      {billingInterval === 'yearly' ? yearlyDiscountPercent : monthlyDiscountPercent}% OFF
                    </span>
                  </div>
                ) : (
                  <div className="discount-badge">
                    {billingInterval === 'yearly' ? yearlyDiscountPercent : monthlyDiscountPercent}% OFF
                  </div>
                )}

                <h2 className="plan-name-selection">{plan.name}</h2>
                <p className="plan-description">{plan.tagline || ''}</p>

                <div className="plan-price-selection">
                  <div className="price-comparison">
                    <span className="original-price">${parseFloat(originalMonthlyPrice).toFixed(2)}</span>
                    <span className="discount-label">Regular Price</span>
                  </div>
                  <div className="price-main">
                    <span className="price-currency">$</span>
                    <span className="price-amount">{billingInterval === 'yearly' ? pricePerMonth : displayPrice}</span>
                    <span className="price-interval">/mo</span>
                  </div>
                  {billingInterval === 'yearly' && plan.yearlyPrice && (
                    <p className="billed-yearly">billed yearly · save ${yearlySaveVsMonthly}</p>
                  )}
                  {billingInterval === 'monthly' && (
                    <p className="savings-text">Save ${(parseFloat(originalMonthlyPrice) - currentMonthlyPrice).toFixed(2)}/month</p>
                  )}
                </div>

                <ul className="plan-features-selection">
                  {plan.features && plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="feature-check"><i className="fa-solid fa-check" /></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {(() => {
                  const planIdx = planOrder.indexOf(plan.slug);
                  const currentSlug = getCurrentPlanSlug();
                  const currentIdx = planOrder.indexOf(currentSlug);
                  const trialEligible = customerStatus === null || customerStatus?.trialEligible || customerStatus?.trialEligibilityByService?.[service];

                  if (changePlanSub) {
                    const isCurrentPlanMatch = currentSlug === plan.slug;
                    const isCurrentBillingMatch = isCurrentPlanMatch && !canChangeBillingInterval(plan.slug);
                    const isHigherTier = planIdx > currentIdx && currentIdx >= 0;
                    const isLowerTier = planIdx < currentIdx && currentIdx >= 0;
                    const isBillingSwitch = isCurrentPlanMatch && canChangeBillingInterval(plan.slug);
                    if (isCurrentBillingMatch) return (
                      <>
                        <button className={`btn-plan-select ${isRecommended ? 'recommended' : ''}`} disabled style={{opacity:0.5,cursor:'not-allowed'}}>Current Plan</button>
                        <p className="urgency-text">Your active plan</p>
                      </>
                    );
                    if (isBillingSwitch) return (
                      <>
                        <button onClick={() => handlePlanSelect(plan.slug, 'billing_change')} className={`btn-plan-select ${isRecommended ? 'recommended' : ''}`}>
                          {billingInterval === 'yearly' ? 'Switch to Yearly Billing' : 'Switch to Monthly Billing'}
                        </button>
                        <p className="urgency-text">Save more with annual billing</p>
                      </>
                    );
                    if (isHigherTier) return (
                      <>
                        <button onClick={() => !hasPendingChange && handlePlanSelect(plan.slug, 'upgrade')} className={`btn-plan-select ${isRecommended ? 'recommended' : ''}`} disabled={hasPendingChange} style={hasPendingChange?{opacity:0.4,cursor:'not-allowed'}:{}}>
                          Upgrade Plan
                        </button>
                        <p className="urgency-text">{hasPendingChange ? 'Plan change pending approval' : 'Upgrade and unlock more features'}</p>
                      </>
                    );
                    if (isLowerTier) return (
                      <>
                        <button onClick={() => !hasPendingChange && handleDowngrade(plan, changePlanSub)} className="btn-plan-select" disabled={hasPendingChange} style={hasPendingChange?{opacity:0.4,cursor:'not-allowed'}:{}}>
                          Downgrade Plan
                        </button>
                        <p className="urgency-text">{hasPendingChange ? 'Plan change pending approval' : 'Switch to a lighter plan'}</p>
                      </>
                    );
                    return (
                      <>
                        <button onClick={() => handlePlanSelect(plan.slug, 'new')} className={`btn-plan-select ${isRecommended ? 'recommended' : ''}`}>Switch to This Plan</button>
                        <p className="urgency-text">Secure your discount today!</p>
                      </>
                    );
                  }

                  const hasSamePlanMatch = getServiceSubscriptions().some(s =>
                    s.planSlug === plan.slug || (s.priceId && (s.priceId === plan.priceId || s.priceId === plan.yearlyPriceId))
                  );
                  let btnLabel = hasSamePlanMatch ? 'Add Another Instance' : plan.trialDays > 0 && trialEligible ? 'Start Free Trial' : 'Get Started';
                  let btnSubtext = hasSamePlanMatch ? 'Scale with an additional instance' : plan.trialDays > 0 && trialEligible ? `${plan.trialDays}-day free trial — no payment required` : `Price locked for ${billingInterval === 'yearly' ? '2 years' : '6 months'}`;

                  return (
                    <>
                      <div className="price-lock-guarantee">
                        <i className="fa-solid fa-lock" style={{fontSize:'10px'}} />
                        <span>Price locked for {billingInterval === 'yearly' ? '2 years' : '6 months'} — guaranteed</span>
                      </div>
                      <button
                        onClick={() => { if (!isAuthenticated()) { navigate(`/login?next=/${service}`); return; } handlePlanSelect(plan.slug, 'new'); }}
                        className={`btn-plan-select ${isRecommended ? 'recommended' : ''}`}
                      >
                        {isAuthenticated() ? btnLabel : (plan.trialDays > 0 ? 'Start Free Trial' : 'Get Started')}
                      </button>
                      <p className="urgency-text">{btnSubtext}</p>
                    </>
                  );
                })()}

                {billingInterval === 'yearly' && plan.yearlyPrice && (
                  <p className="total-yearly">${plan.yearlyPrice} total per year <span style={{color:'#555'}}>(was ${originalYearlyPrice})</span></p>
                )}
                {(() => {
                  const renewPrice = billingInterval === 'yearly' ? (plan.renewalPriceYearly ?? plan.renewalPrice) : plan.renewalPrice;
                  const isTrial = plan.trialDays > 0 && (customerStatus === null || customerStatus?.trialEligible || customerStatus?.trialEligibilityByService?.[service]);
                  return renewPrice != null ? (
                    <p className="renews-at-text">{isTrial ? 'After trial ends, renews' : 'Renews'} at ${Number(renewPrice).toFixed(2)}/{billingInterval === 'yearly' ? 'yr' : 'mo'}</p>
                  ) : null;
                })()}
              </div>
            );
          })}
        </div>

        {/* ── TRUSTPILOT REVIEWS ── */}
        <div className="reviews-section">
          <div className="reviews-glass-header">
            <a href="https://www.trustpilot.com/review/openhosst.com" target="_blank" rel="noopener noreferrer" className="reviews-trustpilot-link">
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                <span className="reviews-rating-score">4.5</span>
                <div className="reviews-stars-row">
                  <i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" /><i className="fa-solid fa-star" />
                </div>
              </div>
              <div className="reviews-label-block">
                <span className="reviews-label-excellent">Excellent</span>
                <span className="reviews-label-platform">on Trustpilot</span>
              </div>
              <img src="https://cdn.trustpilot.net/brand-assets/4.5.0/logo-white.svg" alt="Trustpilot" className="reviews-tp-logo" />
            </a>
            <p className="reviews-count">Based on 100% verified reviews</p>
          </div>
          <div className="reviews-track-wrapper">
            <div className="reviews-track">
              {[...reviews, ...reviews].map((review, i) => (
                <div key={i} className="review-card">
                  <div className="review-stars">{'★'.repeat(review.rating)}</div>
                  <p className="review-title">{review.title}</p>
                  <p className="review-text">"{review.text}"</p>
                  <div className="review-author">
                    <div className="review-avatar">{review.name.charAt(0)}</div>
                    <div>
                      <span className="review-name">{review.name}</span>
                      <div className="review-meta">
                        {review.country && <span className="review-country">{review.country}</span>}
                        <span className="review-date">{review.date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── BOTTOM GUARANTEE BAR ── */}
        <div className="guarantee-bar">
          <div className="guarantee-item">
            <i className="fa-solid fa-shield-halved" style={{color:'#40E0D0'}} />
            <div>
              <strong>Price Lock</strong>
              <span>{billingInterval === 'yearly' ? '2 Year' : '6 Month'} Price Freeze</span>
            </div>
          </div>
          <div className="guarantee-sep" />
          <div className="guarantee-item">
            <i className="fa-solid fa-clock-rotate-left" style={{color:'#4ade80'}} />
            <div>
              <strong>Cancel Anytime</strong>
              <span>No penalties</span>
            </div>
          </div>
          <div className="guarantee-sep" />
          <div className="guarantee-item">
            <i className="fa-solid fa-server" style={{color:'#40E0D0'}} />
            <div>
              <strong>99.9% Uptime</strong>
              <span>Enterprise infrastructure</span>
            </div>
          </div>
          <div className="guarantee-sep" />
          <div className="guarantee-item">
            <i className="fa-brands fa-stripe" style={{color:'#60a5fa'}} />
            <div>
              <strong>Secure Checkout</strong>
              <span>Powered by Stripe</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default PlanSelectionPage;